import { useEffect, useRef, useState } from "react";
import FormLayout from "src/layouts/FormLayout";
import { Col } from "src/components/layout";
import { DomainLandSituationType } from "@porchpass/api-client";
import {
  useResponseRedirect,
  useRedirectToPage,
  useRoleForBorrower,
} from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import {
  fromLand,
  toCreateLandAlreadyOwnedReq,
  toCreateLandCurrentlyFinancedReq,
  toCreateLandLeasingReq,
  toCreateLandPurchasingReq,
  type LandFormData,
} from "src/api/adapters";
import type { DtoGetLandResp } from "@porchpass/api-client";
import {
  EditRoadIcon,
  FormTable,
  FormTableBody,
  LandFields,
  type LandFieldsFormData,
} from "@porchpass/ui";
import {
  fromLandAlreadyOwnedFormData,
  fromLandFinancedFormData,
  fromLandLeasingFormData,
  fromLandPurchasingFormData,
  mergeSavedLandFields,
  toLandFieldsFormData,
} from "src/forms/adapters/land";
import { fieldErrorsAfterSubmit } from "src/forms/fieldErrorsAfterSubmit";
import { validateLandFieldsFormData } from "./landSituationValidation";

export default () => {
  const api = useWebClientApi();
  const role = useRoleForBorrower();
  const coborrowerNavigate = useRedirectToPage("/ethnicity");
  if (role === "COBORROWER") {
    coborrowerNavigate();
  }

  const { formData, hasLoaded } = useFormLoad<LandFormData>(
    {},
    () => api.getLand(),
    (data) => fromLand(data as DtoGetLandResp),
  );

  const [landFieldsValue, setLandFieldsValue] = useState<LandFieldsFormData>({
    landSituation: "",
  });
  const hasInitializedForm = useRef(false);
  // Fields the borrower changed, so seeding from the saved land keeps them
  const editedFields = useRef(new Set<keyof LandFieldsFormData>());
  const landFieldsRef = useRef(landFieldsValue);
  landFieldsRef.current = landFieldsValue;

  useEffect(() => {
    // Wait for the saved land before seeding the form, or it seeds empty
    if (hasLoaded && !hasInitializedForm.current) {
      hasInitializedForm.current = true;
      setLandFieldsValue((current) =>
        mergeSavedLandFields(
          toLandFieldsFormData(formData),
          current,
          editedFields.current,
        ),
      );
    }
  }, [formData, hasLoaded]);

  const [submitFamilyLandHandler, loadingFamily, familyResponse] =
    useDlaFormSubmit(() => api.createLandFamily());
  useResponseRedirect("/home/manufacture-detail", familyResponse);

  const [
    submitReservationLandHandler,
    loadingReservation,
    reservationResponse,
  ] = useDlaFormSubmit(() => api.createLandReservation());
  useResponseRedirect("/home/manufacture-detail", reservationResponse);

  const [submitLandHandler, loadingLand, landResponse, hasAttemptedSubmission] =
    useDlaFormSubmit(async () => {
      const value = landFieldsRef.current;
      switch (value.landSituation) {
        case DomainLandSituationType.LandSituationTypeAlreadyOwned:
          return api.createLandAlreadyOwned(
            toCreateLandAlreadyOwnedReq(fromLandAlreadyOwnedFormData(value)),
          );
        case DomainLandSituationType.LandSituationTypeCurrentlyFinanced:
          return api.createLandCurrentlyFinanced(
            toCreateLandCurrentlyFinancedReq(fromLandFinancedFormData(value)),
          );
        case DomainLandSituationType.LandSituationTypePurchasing:
          return api.createLandPurchasing(
            toCreateLandPurchasingReq(fromLandPurchasingFormData(value)),
          );
        case DomainLandSituationType.LandSituationTypeLeasing:
          return api.createLandLeasing(
            toCreateLandLeasingReq(fromLandLeasingFormData(value)),
          );
        default:
          return Promise.resolve();
      }
    });
  useResponseRedirect("/home/manufacture-detail", landResponse);

  const { fieldErrors } = validateLandFieldsFormData(landFieldsValue);
  const visibleErrors = fieldErrorsAfterSubmit(
    fieldErrors,
    hasAttemptedSubmission,
  );

  const handleSubmit = () => {
    const situation = landFieldsValue.landSituation;
    if (situation === DomainLandSituationType.LandSituationTypeFamily) {
      void submitFamilyLandHandler({});
      return;
    }
    if (situation === DomainLandSituationType.LandSituationReservation) {
      void submitReservationLandHandler({});
      return;
    }

    const { submitErrors } = validateLandFieldsFormData(landFieldsValue);
    void submitLandHandler({}, submitErrors);
  };

  return (
    <FormLayout
      title="What is the land situation for your home?"
      headerTitle="Home and Land"
      stepIndex={7}
      footerTitle="Next"
      clickHandler={handleSubmit}
      isLoading={loadingFamily || loadingReservation || loadingLand}
    >
      <Col>
        <FormTable
          title="Land Details"
          subtitle="Who owns the land where the home will be placed"
          headerIcon={
            <EditRoadIcon
              variant="bulk-rounded"
              size={16}
              className="text-[#6F41E6]"
            />
          }
        >
          <FormTableBody>
            <LandFields
              value={landFieldsValue}
              onChange={(patch) => {
                for (const key of Object.keys(patch)) {
                  editedFields.current.add(key as keyof LandFieldsFormData);
                }
                setLandFieldsValue((current) => ({ ...current, ...patch }));
              }}
              errors={visibleErrors}
            />
          </FormTableBody>
        </FormTable>
      </Col>
    </FormLayout>
  );
};
